// IZI Extended Warranty Plan — Terms & Conditions copy.
// Source of truth: "IZI Extended Warranty Plan TnCs page" design export. Keep wording exact.

import {
  clause,
  contactSection,
  dash,
  note,
  slaSection,
  step,
  type TncHero,
  type TncSection,
} from "@/components/tnc/tncTypes";

export const ewTncHero: TncHero = {
  breadcrumb: "IZI Extended Warranty Plan",
  current: "Terms & Conditions",
  title: "Terms and Conditions",
  lines: [
    "Extended Warranty Plan",
    "For drones purchased on IZI Cart.",
  ],
};

export const ewTncFooterNote =
  "IZI Extended Warranty Plan is administered by EmbedMike India Private Limited (Protega) on behalf of IZI Cart. It is a service contract, not an insurance policy, and the Terms and Conditions govern in full.";

export const ewTncSections: TncSection[] = [
  {
    kind: "clauses",
    id: "plan-introduction",
    title: "Plan introduction",
    blocks: [
      clause(
        "The Protega Extended Warranty (EW) Plan (“Plan”) provides continued coverage for eligible IZI Cart drones beyond the expiry of the manufacturer’s standard warranty, subject to these Terms and Conditions (“Terms”)."
      ),
      clause(
        "This Plan covers mechanical, electrical and component failures arising solely due to manufacturing defects in material or workmanship during normal and intended usage of the drone."
      ),
      clause(
        "Coverage under this Plan begins immediately after the expiry of the manufacturer’s standard warranty and remains valid for the selected Policy Term of one (1) year or two (2) years, as specified in the Certificate of Protection."
      ),
      clause(
        "This Plan is a service contract administered by EmbedMike India Private Limited (“Protega”) on behalf of IZI Cart. It does not constitute an insurance policy and does not replace, extend or override statutory consumer rights. Protega may appoint Authorised Service Partners to perform any part of the Plan and remains responsible to the Customer for its obligations under the Plan."
      ),
      clause(
        "The Plan is valid only within the territorial limits of India and must be purchased together with the drone through the Merchant’s eligible sales channel where the Plan is offered."
      ),
      clause(
        "Coverage under this Plan is applicable only when the drone is operated strictly in accordance with the manufacturer’s user manual, safety instructions, operational limitations, environmental guidelines and intended use cases as specified in official documentation."
      ),
      clause(
        "The Plan covers only the failures listed in clause 6, subject to the exclusions in clause 7.",
        "Scope of cover:"
      ),
      clause(
        "If the manufacturer’s warranty was voided, reduced or denied for any reason, including unauthorised repair, tampering, or an altered or removed serial number, this Plan is void.",
        "Manufacturer’s warranty voided:"
      ),
      clause(
        "Any fault that first appeared during the manufacturer’s warranty period must be claimed from the manufacturer and is not covered under this Plan.",
        "Faults during the manufacturer’s warranty:"
      ),
      clause(
        "If a covered manufacturing defect causes the drone to crash or fall, the Plan covers only the repair or replacement of the defective component. Crash, fall or impact damage to any other part of the drone is not covered. Flight logs are mandatory for any claim involving flight.",
        "Failures that cause a crash:"
      ),
      clause(
        "Each approved claim is subject to a service fee: for a 1-year Plan, ₹1,500 or 5% of the Invoice Value; for a 2-year Plan, ₹2,000 or 7.5% of the Invoice Value, whichever is lower, or as stated in your Certificate of Protection. The service fee must be paid before pickup or service begins and is non-refundable once the RMA is issued.",
        "Service fee:"
      ),
      clause(
        "A replacement unit provided under this Plan does not start a new warranty or Policy Term, and does not extend or reset the Policy Term.",
        "Replacement does not reset the warranty:"
      ),
      clause(
        "The Certificate of Protection, these Terms, the invoice for the drone and the Plan, and the payment confirmation together form the Plan. If the Certificate of Protection and these Terms differ, these Terms prevail.",
        "Documents forming the Plan:"
      ),
      clause(
        "Where a claim is resolved by replacement or Store Credit, depreciation based on the age of the drone from the invoice date applies instead of the service fee, as set out below. The 1-year Plan covers the drone from 12 to 24 months of age, and the 2-year Plan from 12 to 36 months. Store Credit is issued for the Invoice Value less depreciation. For a replacement, the Customer pays the depreciation amount before the replacement is dispatched, or may choose Store Credit instead. Any service fee already paid is adjusted against the depreciation amount or added to the Store Credit. Claims resolved by repair are not subject to depreciation.",
        "Depreciation on replacement or store credit:"
      ),
      {
        kind: "table",
        head: ["Age of drone at claim", "Depreciation"],
        rows: [
          ["12 to 18 months", "15%"],
          ["18 to 24 months", "20%"],
          ["24 to 30 months (2-year Plan only)", "25%"],
          ["30 to 36 months (2-year Plan only)", "30%"],
        ],
      },
    ],
  },
  {
    kind: "clauses",
    id: "customer-responsibilities",
    title: "Customer responsibilities",
    blocks: [
      note("To remain eligible for coverage under this Plan, the customer must:"),
      clause(
        "Retain the original purchase invoice, manufacturer’s warranty documentation and Extended Warranty certificate."
      ),
      clause(
        "Operate the drone strictly in accordance with the manufacturer’s user manual, safety instructions and stated operating limitations."
      ),
      clause(
        "Ensure compliance with all applicable aviation, airspace and regulatory requirements governing drone usage in India."
      ),
      clause(
        "Report any malfunction or failure to Protega within seventy-two (72) hours of the fault first appearing, and stop operating the drone until Protega instructs otherwise."
      ),
      clause(
        "Retain the flight logs from the drone and its app, and provide them when requested."
      ),
      clause(
        "Avoid unauthorised repair, modification, firmware alteration or third-party servicing, and do not hand the drone to any service centre before Protega approves the claim."
      ),
      clause(
        "Avoid negligent, reckless, abusive, experimental or improper operation of the drone."
      ),
      clause(
        "Provide complete, accurate and truthful information regarding the fault."
      ),
      clause(
        "Back up and remove all personal data, footage and memory cards before handing over the drone for inspection or service."
      ),
      clause(
        "Cooperate fully during inspection, diagnostics, pickup and service processes, and hand over the drone complete with its remote controller and all parts supplied with it."
      ),
      clause(
        "Keep your registered email address, mobile number and WhatsApp number up to date."
      ),
      note(
        "Failure to comply with these responsibilities may result in claim rejection or denial of service."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "how-to-file-a-claim",
    title: "How to file a claim",
    blocks: [
      step("Step 1: Contact support"),
      note(
        "Claims must be initiated through official Protega channels. Claims are accepted only through these two channels."
      ),
      dash("www.protega.ai/customer-login", "Portal:"),
      dash("+91 93240 95283", "WhatsApp:"),
      note(
        "Claims must be reported within seventy-two (72) hours of the fault first appearing.",
        true
      ),
      step("Step 2: Submit documentation"),
      dash("Purchase invoice and Extended Warranty certificate"),
      dash("Description of the malfunction, including when it first appeared"),
      dash("Original, unedited photos or videos demonstrating the issue"),
      dash("Flight logs, for any fault that occurred during or affects flight"),
      dash("Drone serial number"),
      note(
        "Protega may request additional information, documents or a live video inspection before approving a claim. A claim will not be processed until the requested information is provided."
      ),
      step("Step 3: Claim verification"),
      note(
        "Protega verifies eligibility, coverage applicability and the nature of the failure. Initial assessment is typically completed within 24–48 working hours of receipt of complete documentation."
      ),
      step("Step 4: Service fee payment"),
      note(
        "Upon claim approval, a secure payment link is shared for payment of the applicable service fee."
      ),
      step("Step 5: RMA and pickup"),
      note(
        "A Return Merchandise Authorization (RMA) is generated and pickup or service is arranged through Protega’s authorised partners."
      ),
      step("Step 6: Resolution"),
      note(
        "The drone is repaired using new or refurbished genuine parts, replaced with a new, refurbished or comparable product, or settled through store credit, subject to assessment. Standard repair cases are typically completed within 5–7 working days; replacement cases are subject to product availability. All timelines are subject to clause 8."
      ),
      clause(
        "The drone must not be repaired, opened or handed to any service centre before Protega approves the claim in writing. Costs incurred before approval will not be reimbursed, and such a claim may be rejected.",
        "Approval before repair:"
      ),
      clause(
        "The service fee must be paid within 7 days of the payment link being sent. No pickup, inspection or repair takes place before payment. If the service fee is not paid within this time, the claim is treated as withdrawn and closed, and cannot be re-opened. No new claim will be accepted for the same fault. Where Protega has already incurred inspection or logistics costs, the Customer bears these costs before the drone is returned.",
        "Service fee deadline:"
      ),
      clause(
        "Claims reported after 72 hours of the fault first appearing may be rejected.",
        "Late reporting:"
      ),
      note(
        "Claim limit: One approved claim per Policy Term. The Plan ends once that claim is resolved.",
        true
      ),
    ],
  },
  {
    kind: "clauses",
    id: "definitions",
    title: "Definitions",
    blocks: [
      clause(
        "The person purchasing an IZI Cart drone with the Plan, whose name appears on the invoice.",
        "Customer:"
      ),
      clause("IZI Cart.", "Merchant:"),
      clause("EmbedMike India Private Limited.", "Administrator / Protega:"),
      clause(
        "The eligible IZI Cart drone and its remote controller supplied in the box, registered under this Plan and identified in the Certificate of Protection.",
        "Covered Product:"
      ),
      clause(
        "The standard warranty supplied with the Covered Product by its manufacturer.",
        "Manufacturer’s Warranty:"
      ),
      clause(
        "A post-warranty malfunction caused by an internal manufacturing defect under normal use.",
        "Mechanical/Electrical Failure:"
      ),
      clause(
        "Operation, handling or exposure demonstrating disregard for manufacturer instructions, safety guidelines, regulatory requirements or known operational limitations.",
        "Gross Negligence:"
      ),
      clause(
        "Use of the Product by the Customer for personal, non-business purposes only.",
        "Personal Use:"
      ),
      clause(
        "Use of the Product for paid work, rental, resale, or by or for a business, institution or office.",
        "Commercial Use:"
      ),
      clause(
        "Duration of the Extended Warranty (1-Year or 2-Year), starting immediately after the manufacturer’s warranty expires.",
        "Policy Term:"
      ),
      clause(
        "The price paid for the drone as shown on the purchase invoice, after any discounts and including applicable taxes, but excluding shipping or delivery charges, convenience or COD fees, installation charges, EMI interest or fees, and the Plan fee.",
        "Invoice Value:"
      ),
      clause(
        "The Invoice Value of the drone. This is the maximum liability under the Plan, across all claims.",
        "Coverage Limit:"
      ),
      clause(
        "The reduction applied to the value of a replacement or Store Credit based on the age of the drone, as set out in clause 1.14.",
        "Depreciation:"
      ),
      clause(
        "The amount payable by the Customer for each approved claim, as stated in clause 1.11.",
        "Service Fee:"
      ),
      clause(
        "The flight records stored by the drone and its app, including flight path, altitude, speed, battery and error data.",
        "Flight Logs:"
      ),
      clause(
        "Return Merchandise Authorization issued after claim approval and service fee payment.",
        "RMA:"
      ),
      clause(
        "Where the repair cost exceeds the Invoice Value, or the drone cannot be repaired.",
        "Beyond Economic Repair (BER):"
      ),
      clause(
        "A non-cash voucher or credit usable only on IZI Cart, issued under clause 8.",
        "Store Credit:"
      ),
      clause(
        "A pincode in India where Protega or its Authorised Service Partners provide pickup or service.",
        "Serviceable Location:"
      ),
    ],
  },
  {
    kind: "clauses",
    id: "eligibility",
    title: "Eligibility",
    blocks: [
      clause(
        "Applicable only to new, genuine IZI Cart drones purchased through authorised IZI Cart sales channels in India and eligible for the Plan at the time of purchase."
      ),
      clause(
        "The Plan must be purchased together with the drone through the Merchant’s eligible sales channel where the Plan is offered."
      ),
      clause("The manufacturer’s warranty must not have been voided."),
      clause(
        "Refurbished, open-box, second-hand and returned-and-resold units are not eligible."
      ),
      clause(
        "The drone is used for Personal Use only. Commercial Use is excluded."
      ),
      clause(
        "The Customer is responsible for purchasing the Plan for the correct drone. Protega may reject a claim if the model or serial number of the drone claimed does not match the Plan."
      ),
      clause(
        "If a Plan is issued on a product that is not eligible, the Plan is void and the Plan fee is refunded in full."
      ),
      clause(
        "Coverage is limited to the drone and its remote controller. Batteries, propellers, propeller guards, landing gear, cables, chargers, mounts and other accessories are not covered."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-covered",
    title: "What is covered",
    blocks: [
      note(
        "Subject to the terms of this Plan, coverage includes the following, only where the failure arises from a manufacturing defect in material or workmanship, as diagnosed by Protega’s authorised service partner:",
        true
      ),
      clause(
        "Motor, propeller hub or arm failure caused by internal manufacturing defects"
      ),
      clause("Gimbal or camera malfunction not resulting from impact or crash"),
      clause("Flight controller, ESC, IMU or sensor circuit failure"),
      clause("Remote controller malfunction due to circuit defects"),
      clause(
        "Charging port, PCB or internal board malfunction due to internal failure"
      ),
      clause(
        "Other internal mechanical or electrical failures of the drone or its remote controller caused by a manufacturing defect"
      ),
      note(
        "The service fee applies to claims resolved by repair. Claims resolved by replacement or store credit are subject to depreciation instead (clause 1.14)."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-not-covered",
    title: "What is not covered",
    blocks: [
      note("This Plan does not cover:"),
      clause(
        "Accidental damage, crashes, falls, collisions or impact, and any external damage."
      ),
      clause("Liquid damage of any kind, including water entry."),
      clause("Damage from fire, pests or natural disasters."),
      clause(
        "Environmental damage caused by dust, moisture, heat or humidity."
      ),
      clause("PCB burns caused by overcharging."),
      clause("Battery swelling caused by improper storage."),
      clause(
        "Wear and tear, including cosmetic wear, scratches, scuffs, propeller damage and material fatigue."
      ),
      clause(
        "Firmware modification, third-party tuning, custom software or software alteration."
      ),
      clause(
        "Unauthorised repairs, disassembly or third-party servicing, or use of unapproved third-party parts."
      ),
      clause("Drones whose serial number has been altered or removed."),
      clause(
        "Misuse, abuse, gross negligence, reckless or improper operation, and modifications."
      ),
      clause(
        "Damage caused by operation outside manufacturer-recommended conditions."
      ),
      clause(
        "Theft, loss, missing products, misplacement or unexplained disappearance."
      ),
      clause(
        "Batteries and accessories, including propellers, cables, chargers and mounts. Where damage to an accessory is directly caused by a covered failure of the drone, Protega may assess it at its discretion."
      ),
      clause("Products not manufactured or sold by IZI."),
      clause(
        "Crash damage caused by a covered fault, beyond the repair of the defective component."
      ),
      clause(
        "Faults that first appeared during the manufacturer’s warranty period."
      ),
      clause("Commercial Use."),
      clause(
        "Consequential or incidental losses, such as loss of use or loss of footage or data."
      ),
      clause(
        "Any claim involving fraud, misrepresentation or concealment of facts."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "service-procedures",
    title: "Service procedures & limit of liability",
    blocks: [
      clause("Only one (1) approved claim is permitted per Policy Term."),
      clause(
        "Maximum liability under this Plan shall not exceed the Invoice Value of the drone, across all claims."
      ),
      clause(
        "Once the approved claim is resolved by repair, replacement or Store Credit, the Plan shall terminate automatically. A repair or replacement does not reset or extend the Policy Term."
      ),
      clause(
        "Repairs are performed exclusively through Protega-authorised service partners using new or refurbished genuine or equivalent components."
      ),
      clause(
        "Replacement units may be new, refurbished or of equal or comparable specifications, at Protega’s discretion, and may differ in colour or design."
      ),
      clause(
        "All decisions relating to repair, replacement or settlement shall be binding, subject to the dispute resolution clause in clause 11."
      ),
      clause(
        "No cash settlement is made under the Plan. Claims are resolved only by repair, replacement or Store Credit."
      ),
      clause(
        "Replacement and Store Credit are subject to depreciation under clause 1.14, which applies instead of the service fee."
      ),
      clause(
        "Store Credit may be issued where repair or a comparable replacement is not feasible. Store Credit is valid for 6 months from the date of issue, usable only on IZI Cart, not transferable and not redeemable for cash."
      ),
      clause(
        "Protega may request submission of the drone or parts for inspection."
      ),
      clause(
        "Replaced parts, and any drone replaced or settled by Store Credit, become the property of Protega."
      ),
      clause(
        "Where the drone is handed over incomplete, the value of missing parts is deducted from the resolution."
      ),
      clause(
        "If inspection finds no fault, or finds that the fault is not covered, the claim is rejected and the Customer bears the inspection and logistics costs before the drone is returned."
      ),
      clause(
        "If the Customer does not accept delivery or respond within 7 days of Protega’s written notice after repair or rejection, Protega may charge reasonable storage costs from that date, and any further delivery attempts will be charged to the Customer. Protega will send a final written notice before disposal, and if the product is still not collected within 10 days of the first notice, Protega may dispose of or recycle it."
      ),
      clause(
        "Where Protega schedules a pickup, doorstep repair visit, or delivery of a repaired or replacement product, the Customer must be available at the time discussed with the Customer before the visit is scheduled, or reschedule at least 24 hours in advance. If the Customer is unavailable, refuses to accept, or does not provide access, the cost of each further attempt is borne by the Customer and must be paid before the next attempt is scheduled. After two missed attempts, Protega may close the claim as withdrawn or, where the product has already been repaired or replaced, apply the storage and disposal terms above.",
        "Missed appointments:"
      ),
      clause(
        "Protega may reset the drone and update its firmware or app during repair, and is not liable for loss of data, footage or settings."
      ),
      clause(
        "If spare parts are no longer available, Protega may resolve the claim by replacement or Store Credit."
      ),
      clause(
        "Protega will make reasonable efforts to meet the timelines in this Plan, but is not liable for delays caused by unavailability of spare parts, logistics disruptions or events beyond its reasonable control."
      ),
      clause(
        "Protega retains the right to recover costs from responsible third parties."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "cancellation",
    title: "Cancellation",
    blocks: [
      clause(
        "Free-look cancellation is permitted within seven (7) days of purchase for a full refund, provided no claim has been filed."
      ),
      clause(
        "If the drone is returned or the order is cancelled, the Plan is cancelled automatically and the Plan fee refunded in full, provided no claim has been raised."
      ),
      clause(
        "The Plan becomes non-cancellable and non-refundable after a claim has been raised, or after the 7-day period."
      ),
      clause(
        "Protega reserves the right to cancel coverage in cases of fraud, misrepresentation or non-payment. On cancellation for fraud or misrepresentation, the Plan fee is forfeited and Protega may recover all costs incurred on any claim."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "conditions",
    title: "Conditions",
    blocks: [
      clause(
        "This Plan is non-transferable and applies only to the original purchaser and the covered drone."
      ),
      clause("Renewal is not available after Plan expiry."),
      clause(
        "Valid only within India. Pickup and on-site service are available only at Serviceable Locations; elsewhere, Protega may arrange courier pickup or ask the Customer to send the drone."
      ),
      clause(
        "Protega may request additional documentation or inspection where required."
      ),
      clause("Violation of Plan terms may result in denial of service."),
      clause(
        "All notices and communication are sent to the Customer’s registered email address, mobile number or WhatsApp number and are treated as received when sent."
      ),
      clause(
        "Protega may appoint Authorised Service Partners to perform any part of the Plan."
      ),
      clause(
        "Protega is not liable for any failure or delay caused by events beyond its reasonable control, including natural disasters, epidemics, government action, strikes or disruption to logistics."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "legal-disclosures",
    title: "Legal disclosures",
    blocks: [
      clause(
        "This Plan is governed by the laws of India. Subject to arbitration, courts at Mumbai have exclusive jurisdiction."
      ),
      clause(
        "Disputes shall be resolved through arbitration under the Arbitration and Conciliation Act, 1996, by a sole arbitrator appointed by mutual agreement or as provided under that Act. The seat and venue of arbitration is Mumbai and the language is English."
      ),
      clause(
        "Nothing in these Terms limits the Customer’s rights or remedies under the Consumer Protection Act, 2019."
      ),
      clause(
        "Protega and IZI Cart are not liable for indirect, incidental or consequential losses."
      ),
      clause(
        "This document constitutes the entire agreement between the parties. If any provision is held invalid, the remaining provisions remain enforceable."
      ),
      clause(
        "Customer data is processed in accordance with Protega’s Privacy Policy. Personal data, claim photos, videos and flight logs are shared with IZI Cart and Authorised Service Partners only to administer the Plan, verify claims and prevent fraud. Calls and WhatsApp conversations may be recorded for quality and verification."
      ),
      clause(
        "Where Protega relies on an exclusion to reject a claim, the Customer must show that the claim is covered."
      ),
      clause(
        "The Customer indemnifies Protega against loss caused by the Customer’s fraud, misrepresentation or breach of these Terms."
      ),
      clause(
        "Protega may amend these Terms as needed, following regulatory requirements. Amendments will not reduce cover on Plans already issued, except where required by law."
      ),
      clause(
        "A failure or delay by Protega to enforce any term is not a waiver of that term."
      ),
    ],
  },
  {
    kind: "faq",
    id: "faq",
    title: "FAQ",
    items: [
      {
        q: "When does my Extended Warranty coverage start?",
        a: "The Extended Warranty coverage begins immediately after the expiry of the manufacturer’s standard warranty and remains valid for the selected Policy Term.",
      },
      {
        q: "Does this Plan cover accidental damage, crashes or impact-related incidents?",
        a: "No. Accidental damage, crashes, falls, collisions or impact-related incidents are not covered under the Extended Warranty Plan.",
      },
      {
        q: "What if a defect causes my drone to crash?",
        a: "The Plan covers only the repair of the defective component. Crash damage to other parts is not covered. Flight logs are required.",
      },
      {
        q: "Are batteries, propellers or accessories covered?",
        a: "No. Batteries, propellers, cables, chargers, mounts and other accessories are not covered. However, if damage to an accessory is directly caused by a covered internal failure of the drone, Protega may assess it at its discretion, subject to verification and the Plan terms.",
      },
      {
        q: "How many claims can I make under the Extended Warranty Plan?",
        a: "You are eligible for one (1) approved claim per Policy Term. The Plan ends once that claim is resolved, whether by repair, replacement or store credit.",
      },
      {
        q: "Does a replacement restart my warranty?",
        a: "No. A replacement does not start a new warranty or extend the Policy Term.",
      },
      {
        q: "Is there a service fee?",
        a: "Yes. ₹1,500 or 5% of the Invoice Value for a 1-year Plan, and ₹2,000 or 7.5% for a 2-year Plan, whichever is lower, payable within 7 days of claim approval.",
      },
      {
        q: "How soon should I report a fault?",
        a: "Within 72 hours of the fault first appearing. Stop flying the drone until Protega advises.",
      },
      {
        q: "Can I cancel my Extended Warranty Plan?",
        a: "Yes. You may cancel the Plan within seven (7) days of purchase, provided no claim has been filed. After this period, the Plan becomes non-refundable.",
      },
      {
        q: "What if I return my drone?",
        a: "The Plan is cancelled automatically and the Plan fee is refunded, provided no claim has been raised.",
      },
      {
        q: "Is commercial or professional drone usage covered under this Plan?",
        a: "No. The Plan applies to Personal Use only. Commercial Use is excluded.",
      },
      {
        q: "Is depreciation applied on a replacement?",
        a: "Yes. Replacement or store credit is reduced by depreciation based on the drone’s age, instead of the service fee: 15% at 12–18 months, 20% at 18–24 months, and on a 2-year Plan 25% at 24–30 months and 30% at 30–36 months. Repairs are not depreciated.",
      },
      {
        q: "Can I get cash instead of a repair?",
        a: "No. Claims are resolved only by repair, replacement or store credit.",
      },
    ],
  },
  slaSection,
  {
    kind: "text",
    id: "grievance-redressal",
    title: "Grievance redressal",
    text: "If you are not satisfied with a claim decision or with our service, write to support@protega.ai with your Policy ID. Every complaint receives a ticket number, is acknowledged within 48 hours and is resolved within 30 days.",
  },
  contactSection,
];
